import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Drawer, Input, Field, Select, PowerSelect } from '@itds/elements/index.js';
import { NOTIFICATION_TRIGGERS } from '@itds/Constants';

import { Api } from '@brokerage/utils/Api';

const EditNotificationTrigger = ({ member, onClose, onSave, products, show, trigger }) => {
  const [data, setData] = useState({
    agency_id: '',
    emails: [],
    name: '',
    notification_type: '',
    product_id: '',
    trigger: '',
    web_call_url: ''
  });
  const [errors, setErrors] = useState([]);
  const [save_error, setSaveError] = useState(false);

  useEffect(() => {
    setData({
      ...trigger,
      emails: trigger.emails ? trigger.emails.map(e => ({ value: e, label: e })) : []
    });
    setErrors([]);
  }, [trigger]);

  const handleInputChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });
  };

  const handleProductChange = e => {
    if (e.target.value === '') {
      setData({ ...data, product_id: e.target.value, product_type: '' });
    } else {
      // eslint-disable-next-line eqeqeq
      const product = products.find(p => p.id == e.target.value);

      setData({ ...data, product_id: e.target.value, product_type: product.type });
    }
  };

  const handlePowerSelectChange = selected => {
    setData({ ...data, emails: selected });
  };

  const handleSave = () => {
    const errors = [];

    if (!data.agency_id) {
      errors.push('agency_id');
    }

    if (!data.name) {
      errors.push('name');
    }

    if (!data.trigger) {
      errors.push('trigger');
    }

    if (!data.notification_type) {
      errors.push('notification_type');
    }

    if (data.notification_type === 'web_call' && !data.web_call_url) {
      errors.push('web_call_url');
    }

    setErrors(errors);

    if (!errors.length) {
      Api.saveNotificationTrigger(trigger.id, {
        ...data,
        emails: data.emails.map(e => e.value),
        owner_id: member.id
      })
        .then(() => {
          onSave();
          setSaveError(false);
        })
        .catch(() => {
          setSaveError(true);
          setTimeout(() => {
            setSaveError(false);
          }, 2000);
        });
    }
  };

  return (
    <Drawer
      alignRight={true}
      footer={
        <Button onClick={handleSave}>
          {trigger.id ? 'Save Changes' : 'Create New Notification Trigger'}
        </Button>
      }
      onClose={onClose}
      show={show}
      title={trigger.id ? 'Editing Notification Trigger' : 'New Notification Trigger'}
    >
      {save_error ? (
        <Alert type='error'>An error occurred while trying to save the notification trigger.</Alert>
      ) : null}

      <Field label='Agency'>
        <Select
          invalid={errors.includes('agency_id')}
          onChange={handleInputChange.bind(null, 'agency_id')}
          options={[{ value: '', label: '-- Select One --' }].concat(
            member.agencies.map(a => ({ value: a.id, label: a.name }))
          )}
          required={true}
          value={data.agency_id}
        />
      </Field>

      <Field label='Name'>
        <Input
          invalid={errors.includes('name')}
          onChange={handleInputChange.bind(null, 'name')}
          required={true}
          value={data.name}
        />
      </Field>

      <Field label='Event'>
        <Select
          invalid={errors.includes('trigger')}
          onChange={handleInputChange.bind(null, 'trigger')}
          options={[{ value: '', label: '-- Select One --' }].concat(NOTIFICATION_TRIGGERS)}
          required={true}
          value={data.trigger}
        />
      </Field>

      <Field label='Delivery Method'>
        <Select
          invalid={errors.includes('notification_type')}
          onChange={handleInputChange.bind(null, 'notification_type')}
          options={[
            { value: '', label: '-- Select One --' },
            { value: 'email', label: 'Email' },
            { value: 'web_call', label: 'Web Call' }
          ]}
          required={true}
          value={data.notification_type}
        />
      </Field>

      <Field label='Product'>
        <Select
          onChange={handleProductChange}
          options={products.map(p => ({ value: p.id, label: `${p.name || p.id} (${p.type})` }))}
          value={data.product_id}
        />
      </Field>

      {data.notification_type === 'web_call' ? (
        <Field label='Web Call URL'>
          <Input
            invalid={errors.includes('web_call_url')}
            onChange={handleInputChange.bind(null, 'web_call_url')}
            value={data.web_call_url}
          />
        </Field>
      ) : null}

      {data.notification_type === 'email' ? (
        <Field label='Emails'>
          <PowerSelect
            creatable={true}
            isMulti={true}
            noOptionsMessage='Invalid email, keep typing...'
            onChange={handlePowerSelectChange}
            placeholder='Add some emails...'
            value={data.emails}
          />
        </Field>
      ) : null}
    </Drawer>
  );
};

EditNotificationTrigger.propTypes = {
  member: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  products: PropTypes.array,
  show: PropTypes.bool,
  trigger: PropTypes.object
};

export default EditNotificationTrigger;
